import { Button, Container, IconButton, Paper } from "@material-ui/core";
import {
    Add as AddIcon,
    ArrowDownward as ArrowDownwardIcon,
    ArrowUpward as ArrowUpwardIcon,
    Close as CloseIcon,
    Save as SaveIcon,
} from "@material-ui/icons";
import { FieldArray, Form, Formik } from "formik";
import { Fragment, useMemo } from "react";
import { useSelector } from "react-redux";
import { Prompt } from "react-router-dom";
import LoadingComponent from "../../../components/loading";
import * as Yup from "yup";
import yupSchema from "../../../constants/schema";
import { FTextField } from "../../../components/form-elements";
import { makeStyles, createStyles } from "@material-ui/core/styles";
import appService from "../../../services/app.service";
import { useErrorHandler } from "../../../hooks";
import { useSnackbar } from "notistack";

const schema = Yup.object().shape({
    categories: yupSchema.faqCategories,
});
const emptyItem = () => ({
    id: Math.random(),
    title: "",
    description: "",
});
const emptyAboutUs = () => ({
    id: Math.random(),
    title: "",
    items: [emptyItem()],
});

export default function AboutUs() {
    const { initials } = useSelector((s) => s.app);
    const errorHandler = useErrorHandler();
    const { enqueueSnackbar } = useSnackbar();

    const initialValues = useMemo(() => {
        return initials?.aboutUs || { aboutUs: emptyAboutUs() };
    }, [initials]);

    function addItem(arrayHelpers) {
        arrayHelpers.unshift(emptyItem());
    }
    function removeItem(arrayHelpers, item, itemIndex) {
        arrayHelpers.remove(itemIndex);
    }

    async function handleSubmit(values, helpers) {
        const { error } = await appService.updateAboutUs(values);
        if (error) {
            return errorHandler(error);
        }
        enqueueSnackbar("عملیات با موفقیت انجام شد.", { variant: "success" });
        helpers.resetForm({ values });
        appService.getInitials();
    }

    return (
        <Container className="my-2">
            <Formik
                initialValues={initialValues}
                onSubmit={handleSubmit}
                validationSchema={schema}
            >
                {({ isSubmitting, values, dirty, ...args }) => (
                    <LoadingComponent overlay isLoading={isSubmitting}>
                        <Form>
                            <div className="d-flex justify-space-between align-center">
                                <h1>درباره ی ما</h1>
                                <div>
                                    <Button
                                        disabled={!dirty || isSubmitting}
                                        type="submit"
                                        variant="contained"
                                        color="primary"
                                        style={{ marginRight: "auto" }}
                                        startIcon={<SaveIcon />}
                                    >
                                        ذخیره
                                    </Button>
                                </div>
                            </div>
                            <FieldArray
                                name={`aboutUs.items`}
                                render={(arrayHelpers) => {
                                    const aboutUs = values.aboutUs;
                                    return (
                                        <Paper className="my-2 p-2 relative">
                                            <div className="d-flex mb-3 align-center">
                                                <IconButton
                                                    className="ms-2"
                                                    onClick={() => addItem(arrayHelpers)}
                                                >
                                                    <AddIcon />
                                                </IconButton>
                                            </div>
                                            {aboutUs.items.map((_, itemIndex) => {
                                                const item = aboutUs.items[itemIndex];
                                                return (
                                                    <Paper
                                                        variant="outlined"
                                                        className="p-2 mb-3 relative"
                                                        key={item.id}
                                                    >
                                                        <ArrowButtons
                                                            length={aboutUs.items.length}
                                                            arrayHelpers={arrayHelpers}
                                                            index={itemIndex}
                                                        />
                                                        <div className="d-flex mb-3 align-center">
                                                            <FTextField
                                                                label="عنوان"
                                                                className="flex-1"
                                                                fullWidth
                                                                variant="outlined"
                                                                name={`aboutUs.items[${itemIndex}].title`}
                                                            />
                                                            {aboutUs.items.length > 1 && (
                                                                <IconButton
                                                                    className="shrink-0 ms-2"
                                                                    onClick={() =>
                                                                        removeItem(
                                                                            arrayHelpers,
                                                                            item,
                                                                            itemIndex
                                                                        )
                                                                    }
                                                                >
                                                                    <CloseIcon />
                                                                </IconButton>
                                                            )}
                                                        </div>
                                                        <div>
                                                            <FTextField
                                                                label="توضیحات"
                                                                fullWidth
                                                                multiline
                                                                rows={10}
                                                                variant="outlined"
                                                                name={`aboutUs.items[${itemIndex}].description`}
                                                            />
                                                        </div>
                                                    </Paper>
                                                );
                                            })}
                                        </Paper>
                                    );
                                }}
                            />
                        </Form>
                        <Prompt
                            when={dirty}
                            message={() => `تغییرات شما ذخیره نشده. میخواهید خارج شوید؟`}
                        />
                    </LoadingComponent>
                )}
            </Formik>
        </Container>
    );
}

const useStyles = makeStyles((theme) =>
    createStyles({
        arrowsWrapper: {
            position: "absolute",
            insetInlineStart: 0,
            transform: "translateX(-50%)",
            display: "flex",
            flexDirection: "column",
            top: theme.spacing(3),
        },
        arrowButton: {
            background: theme.palette.background.paper,
            margin: 2,
            //   boxShadow: theme.shadows[2],
        },
    })
);
function ArrowButtons({ arrayHelpers, index, length }) {
    const classes = useStyles();
    if (length < 2) return null;
    return (
        <div className={classes.arrowsWrapper}>
            {!!index && (
                <IconButton
                    onClick={() => arrayHelpers.move(index, index - 1)}
                    className={classes.arrowButton}
                    size="small"
                >
                    <ArrowUpwardIcon />
                </IconButton>
            )}
            {index !== length - 1 && (
                <IconButton
                    onClick={() => arrayHelpers.move(index, index + 1)}
                    className={classes.arrowButton}
                    size="small"
                >
                    <ArrowDownwardIcon />
                </IconButton>
            )}
        </div>
    );
}
